import React, { useMemo } from 'react';
import {
  Button,
  Card,
  Group,
  NumberInput,
  Select,
  Title,
  Text,
  Space,
  Grid,
  Checkbox,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { ConfiguredForm } from './formConfiguration';
import { useMediaQuery } from '@mantine/hooks';

type ChatTariffsProps = {
  form: ConfiguredForm;
  onSubmit: () => void;
};

// Выбор: 30 минут, 1 час, 3 часа, 7 часов, 1 день, 3 дня, 5 дней, 1 неделя, 2 недели, 3 недели, 4 недели
const shortDurations = [
  { labelKey: 'common.periods.30min', value: '30' },
  { labelKey: 'common.periods.1hour', value: '60' },
  { labelKey: 'common.periods.3hour', value: '90' },
  { labelKey: 'common.periods.7hour', value: String(7 * 60) },
  { labelKey: 'common.periods.1day', value: String(24 * 60) },
  { labelKey: 'common.periods.3day', value: String(3 * 24 * 60) },
  { labelKey: 'common.periods.5day', value: String(5 * 24 * 60) },
];

// Выбор: 1 день, 3 дня, 5 дней, 1 неделя, 2 недели, 3 недели, 4 недели
const longDurations = [
  { labelKey: 'common.periods.1week', value: String(7 * 24 * 60) },
  { labelKey: 'common.periods.2week', value: String(2 * 7 * 24 * 60) },
  { labelKey: 'common.periods.3week', value: String(3 * 7 * 24 * 60) },
  { labelKey: 'common.periods.1month', value: String(43200) },
  { labelKey: 'common.periods.1year', value: String(525600) },
];

const labelKeyOfDuration = new Map(
  [...shortDurations, ...longDurations].map(({ value, labelKey }) => [value, labelKey])
);

const translateDurations = (durations: typeof shortDurations, t: any) =>
  durations.map(({ labelKey, value }) => ({ label: t(labelKey), value }));

const chatTariffsConfigs = (t: any) => [
  {
    title: t('chat.tariffs.short.title'),
    description: t('chat.tariffs.short.description'),
    description2: t('chat.tariffs.short.description2'),
    durationOptions: translateDurations(shortDurations, t),
  },
  {
    title: t('chat.tariffs.long.title'),
    description: t('chat.tariffs.short.description'),
    description2: t('chat.tariffs.short.description2'),
    durationOptions: translateDurations(longDurations, t),
  },
];

export function getTariffName(value: string, t: any) {
  const labelKey = labelKeyOfDuration.get(value)
  return labelKey ? t(labelKey) : value
}

export const ChatTariffs: React.FC<ChatTariffsProps> = ({ form, onSubmit }) => {
  const { t } = useTranslation()
  const isDesktop = useMediaQuery('(min-width: 576px)');

  const tariffs = useMemo(() => chatTariffsConfigs(t), [t])

  const requiredTariffConfig = tariffs[0];
  const optionalTariffConfig = tariffs[1];
  // Dirty :sigh:
  const isOptionalTariffRequired = form.values.chat[1].is_active;
  return (
    <>
      <Title order={3}>{t('chat.subtitle')}</Title>
      <Space h={'md'} />
      <Text color="gray" size="sm">{t('chat.text')}</Text>
      <Space h={'md'} />
      <Grid>
        {/* Required tariff */}
        <Grid.Col sm={12} lg={6}>
          <Card shadow="md" px="md">
            <Title order={4}>{requiredTariffConfig.title}</Title>
            <Text mt="xs" size={'sm'} color={'gray'}>
              {requiredTariffConfig.description}
            </Text>
            <Text mt="xs" size={'sm'} color={'gray'}>
              {requiredTariffConfig.description2}
            </Text>
            <Space h={'md'} />
            <Checkbox
              label={t('chat.tariffs.inputs.enableTariff.label')}
              color="blue"
              size="md"
              {...form.getListInputProps('chat', 0, 'is_active', { type: 'checkbox' })}
            />
            {form.getListInputProps('chat', 0, 'is_active', { type: 'checkbox' }).checked && (<>
              <Select
                required
                label={t('chat.tariffs.inputs.duration.label')}
                placeholder={t('chat.tariffs.inputs.duration.placeholder')}
                data={requiredTariffConfig.durationOptions}
                {...form.getListInputProps('chat', 0, 'duration')}
                // @ts-ignore Wrong library types for list fields.
                onFocus={() => form.clearFieldError(`chat.0.duration`)}
              />
              <Space h={'md'} />
              <NumberInput
                label={t('chat.tariffs.inputs.price.label')}
                placeholder={t('chat.tariffs.inputs.price.placeholder')}
                required
                hideControls
                rightSection={
                  <div style={{ marginRight: 16 }}>
                    <Text color="gray" size="sm">
                      €
                    </Text>
                  </div>
                }
                {...form.getListInputProps('chat', 0, 'price')}
                // @ts-ignore Wrong library types for list fields.
                onFocus={() => form.clearFieldError(`chat.0.price`)}
              />
            </>)}
          </Card>
        </Grid.Col>

        {/* Optional Tariff*/}
        <Grid.Col sm={12} lg={6}>
          <Card shadow="md" px="md">
            <Title order={4}>{optionalTariffConfig.title}</Title>
            <Text mt="xs" size={'sm'} color={'gray'}>
              {optionalTariffConfig.description}
            </Text>
            <Text mt="xs" size={'sm'} color={'gray'}>
              {optionalTariffConfig.description2}
            </Text>
            <Space h={'md'} />
            <Checkbox
              label={t('chat.tariffs.inputs.enableTariff.label')}
              color="blue"
              size="md"
              {...form.getListInputProps('chat', 1, 'is_active', { type: 'checkbox' })}
            />
            {form.getListInputProps('chat', 1, 'is_active', { type: 'checkbox' }).checked && (<>
              <Select
                required={isOptionalTariffRequired}
                label={t('chat.tariffs.inputs.duration.label')}
                placeholder={t('chat.tariffs.inputs.duration.placeholder')}
                data={optionalTariffConfig.durationOptions}
                {...form.getListInputProps('chat', 1, 'duration')}
                // @ts-ignore Wrong library types for list fields.
                onFocus={() => form.clearFieldError(`chat.1.duration`)}
              />
              <Space h={'md'} />
              <NumberInput
                label={t('chat.tariffs.inputs.price.label')}
                placeholder={t('chat.tariffs.inputs.price.placeholder')}
                required={isOptionalTariffRequired}
                hideControls
                rightSection={
                  <div style={{ marginRight: 16 }}>
                    <Text color="gray" size="sm">
                      €
                    </Text>
                  </div>
                }
                {...form.getListInputProps('chat', 1, 'price')}
                // @ts-ignore Wrong library types for list fields.
                onFocus={() => form.clearFieldError(`chat.1.price`)}
              />
            </>)}
          </Card>
        </Grid.Col>
      </Grid>
      {form.errors.chat && (
        <>
          <Space h={'md'} />
          <Text color="red" size="sm">
            {form.errors.chat}
          </Text>
        </>
      )}
      <Group my="xl" position="right">
        <Button size="md" fullWidth={!isDesktop} color="blue" onClick={onSubmit}>
          {t('common.buttons.next')}
        </Button>
      </Group>
    </>
  );
};

